/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useRef, useEffect, useMemo } from 'react';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbFolder } from '~src/types/db';
import { arrToTree, TableEntry } from '~src/utils/securityGroupsConfig';

interface DraggableFolderListProps {
  folders: DbFolder[];
}

export const DraggableFolderList: React.FC<DraggableFolderListProps> = ({
  folders,
}) => {
  const styles = useStyles(styleFn);

  const sortedFolders = useMemo(() => {
    return [...folders].sort((a, b) => a.name.localeCompare(b.name));
  }, [folders]);

  const treeData = useMemo(() => {
    return arrToTree(sortedFolders, null);
  }, [sortedFolders]);

  return (
    <div css={styles.container}>
      {treeData.map((folder) => (
        <DraggableFolderItem key={folder.key} folder={folder} level={0} />
      ))}
    </div>
  );
};

interface DraggableFolderItemProps {
  folder: TableEntry;
  level: number;
}

const DraggableFolderItem: React.FC<DraggableFolderItemProps> = ({
  folder,
  level,
}) => {
  const styles = useStyles(styleFn);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    return draggable({
      element: el,
      getInitialData: () => ({ folder: folder.dbFolder }),
    });
  }, [folder]);

  return (
    <div css={styles.itemWrapper}>
      <div ref={ref} css={[styles.item, { paddingLeft: level * 20 + 12 }]}>
        <FontAwesomeIcon icon={['fas', 'folder']} css={styles.folderIcon} />
        <span css={styles.folderName}>{folder.title}</span>
        <FontAwesomeIcon
          icon={['fas', 'grip-vertical']}
          css={styles.dragIcon}
        />
      </div>
      {folder.children && folder.children.length > 0 && (
        <div css={styles.children}>
          {folder.children.map((child) => (
            <DraggableFolderItem
              key={child.key}
              folder={child}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  itemWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px',
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 6,
    cursor: 'grab',
    transition: 'all 0.2s ease',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: theme.colors.primaryLight,
      borderColor: theme.colors.primary,
      transform: 'translateX(2px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    '&:active': {
      cursor: 'grabbing',
      transform: 'scale(1.02)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      opacity: 0.8,
    },
  },
  folderIcon: {
    fontSize: 16,
    color: theme.colors.warning,
    flexShrink: 0,
  },
  folderName: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.text.primary,
  },
  dragIcon: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    opacity: 0.6,
  },
  children: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});
